"use client";
import React, { MouseEvent, useCallback, useEffect } from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { StoreDetailsPageProps } from "~/bff/components/StoreDetailsPage";
import { NearbyStoresProps } from "~/components/nearby-stores/component";
import { useHandleRedirectFromStoresInPage } from "~/components/store-details-page/hooks/use-handle-redirect-from-stores-in-page";
import { DataLayerData, EventsNames, EventTypes } from "~/constants/data-layer";
import { Variables } from "~/constants/request";
import { STORE_LOCATOR_SEARCH_TEXT } from "~/constants/session-storage";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { getCountryCode } from "~/helpers/country/get-country-code";
import { importComponentByName } from "~/helpers/import-component-by-name";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { getItem, removeItem } from "~/hooks/use-session-storage/hook";

import { SeoMetaTags } from "../seo-meta-tags/component";

import { About } from "./components/about/component";
import { MainDetails } from "./components/main-details/component";
import { StoreDepartmentsProps } from "./components/store-departments/component";
import { StoreServices } from "./components/store-services/component";
import { WeAcceptProps } from "./components/we-accept/types";
import { getJsonLdConfig } from "./helpers";

const WeAccept = dynamic<WeAcceptProps>(async () =>
  importComponentByName(import("./components/we-accept/component"), "WeAccept"),
);

const StoreDepartments = dynamic<StoreDepartmentsProps>(async () =>
  importComponentByName(
    import("./components/store-departments/component"),
    "StoreDepartments",
  ),
);
const NearbyStores = dynamic<NearbyStoresProps>(async () =>
  importComponentByName(
    import("~/components/nearby-stores/component"),
    "NearbyStores",
  ),
);

export const StoreDetailsPage: React.FC<StoreDetailsPageProps> = ({
  seo,
  storeDetails,
  translations,
  children,
  testAutomationId = "store-details-page",
}) => {
  const isUserAuthenticated = useIsAuthenticated();
  const { locale } = useParams<GlobalPathParams>();
  const logoImage = useVariable<string>(Variables.LOGO_IMAGE_URL);
  const { departments } = storeDetails ?? {};
  const {
    aboutText,
    showMoreText,
    storeDepartmentsText,
    weAccept,
    nearbyStores,
    nearbyStoreOpenedUntil,
    nearbyStoreOpensAt,
  } = translations ?? {};

  // specific GA logic when user was redirected from stores in page
  useHandleRedirectFromStoresInPage(storeDetails);

  const pageUrl = useVariable<string>(Variables.PAGE_URL);

  const jsonLDConfig = getJsonLdConfig(storeDetails, pageUrl, logoImage);

  const dataForGA = useCallback(() => {
    const country = getCountryCode(locale);
    const storeSearchedInStoreLocator =
      getItem(STORE_LOCATOR_SEARCH_TEXT) ?? undefined;
    return {
      [DataLayerData.siteCountry]: country,
      [DataLayerData.type]: EventTypes.storeLocator,
      [DataLayerData.pageCategory]: "store_locator",
      [DataLayerData.loggedInStatus]: isUserAuthenticated,
      [DataLayerData.storeSearch]: storeSearchedInStoreLocator,
      [DataLayerData.storeLocatorStoreSelected]:
        storeDetails?.geomodifier ?? undefined,
    };
  }, [isUserAuthenticated, locale, storeDetails?.geomodifier]);

  useEffect(() => {
    const eventData = {
      [DataLayerData.event]: EventsNames.storeLocatorDetails,
      ...dataForGA(),
    };
    pushDataLayer(eventData);
  }, [dataForGA]);

  useEffect(() => {
    return () => {
      removeItem(STORE_LOCATOR_SEARCH_TEXT);
    };
  }, []);

  const handleGetDirectionsClick = (
    e: MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    e.preventDefault();
    const eventData = {
      [DataLayerData.event]: EventsNames.storeLocatorFindDirections,
      ...dataForGA(),
    };
    pushDataLayer(eventData);
    // eslint-disable-next-line no-restricted-globals
    window.open(url, "_blank");
  };

  return (
    <Box {...getTestAutomationProps(testAutomationId)}>
      {seo && <SeoMetaTags {...seo} extraProps={{ jsonLDConfig }} />}
      <MainDetails
        store={storeDetails}
        translations={translations}
        handleGetDirectionsClick={handleGetDirectionsClick}
      />
      <StoreServices store={storeDetails} translations={translations} />
      <About
        store={storeDetails}
        aboutText={aboutText ?? ""}
        showMoreText={showMoreText ?? ""}
      />
      <StoreDepartments
        departments={departments ?? undefined}
        storeDepartmentsText={storeDepartmentsText ?? ""}
      />
      <WeAccept store={storeDetails} weAcceptText={weAccept ?? ""} />
      <NearbyStores
        store={storeDetails}
        nearbyStoresText={nearbyStores ?? ""}
        storeOpenText={nearbyStoreOpenedUntil ?? ""}
        storeClosedText={nearbyStoreOpensAt ?? ""}
      />
      {children as React.ReactNode}
    </Box>
  );
};
